import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";

/**
 * Shared GET-and-parse for the org-scoped tag search endpoints
 * (`v1/datasets/tags`, `v1/events/tags`, `v1/files/tags`). Each endpoint
 * takes the same `search` / `limit` query params, requires the
 * resource-owner header, and returns the same `PaginatedResponse<string>`
 * wire shape — this factors that identical
 * params → URLSearchParams → GET → parse sequence out of `DatasetService`,
 * `EventService`, and `FileService`, which otherwise hand-rolled it three
 * times over.
 *
 * Callers are expected to have already validated `options.resourceOwnerId`
 * (each service raises its own method-specific error message when it's
 * missing), so this helper takes it as required.
 */
export async function getPaginatedTagsForOrg(
  httpClient: HttpClient,
  path: string,
  params: { search?: string; limit?: number },
  options: { resourceOwnerId: string; abortSignal?: AbortSignal },
): Promise<PaginatedResponse<string>> {
  const searchParams = new URLSearchParams();
  if (params.search) {
    searchParams.append("search", params.search);
  }
  if (params.limit !== undefined) {
    searchParams.append("limit", params.limit.toString());
  }

  const requestUrl = httpClient.constructUrl(path, searchParams);
  const response = await httpClient.get(requestUrl, {
    signal: options.abortSignal,
    headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
  });

  return response.json<PaginatedResponse<string>>();
}
